var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var DateAggregateConfig_1;
/* eslint-disable import/no-duplicates */
import '@symon-ai/wisepipe-base-pipe'; // force import for JsonSchemaV2
import { AggregateColumn, DateFrequency, ToolType } from '@symon-ai/wisepipe-base-pipe';
import { Column } from '@symon-ai/wisepipe-common-data';
import { JSON, JsonDiscriminatorValue, JsonSchemaV2 } from '@symon-ai/wisepipe-serialize';
import { ToolCategories } from './toolCategories';
import { ToolConfig } from './toolConfig';
import { ToolConfigInfo } from './toolConfigInfo';
import { ToolScopeBuilder } from './toolScopeBuilder';
import { ToolValidationResult } from './validation/toolValidationResult';
import { validateSourceWithDetail } from './validation/validateHelper';
import { dateAggregateConfigSchemaValidation } from '../validations.generated';
export const dateAggregateConfigSchema = {
    $id: 'urn:wisepipe-common-pipe:DateAggregateConfig',
    type: 'object',
    properties: {
        dateColumn: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                },
                { type: 'string', minLength: 1 }
            ]
        },
        frequency: {
            oneOf: [
                {
                    $ref: 'urn:wisepipe-base-pipe:EnumToolVariable'
                },
                { type: 'string', enum: Object.values(DateFrequency) }
            ]
        },
        aggColumn: { $ref: 'urn:wisepipe-base-pipe:AggregateColumn' },
        groupByColumns: {
            type: 'array',
            items: {
                oneOf: [
                    {
                        $ref: 'urn:wisepipe-base-pipe:ColumnToolVariable'
                    },
                    { type: 'string' }
                ]
            },
            uniqueItems: true
        }
    },
    required: ['dateColumn', 'frequency', 'aggColumn']
};
let DateAggregateConfig = DateAggregateConfig_1 = class DateAggregateConfig extends ToolConfig {
    constructor(dateColumn, frequency, aggColumn, groupByColumns, customFormats) {
        super(ToolType.DATE_AGGREGATE, customFormats);
        this.dateColumn = dateColumn;
        this.frequency = frequency;
        this.aggColumn = aggColumn;
        if (groupByColumns !== undefined)
            this.groupByColumns = groupByColumns;
        JSON.validate(this, DateAggregateConfig_1);
    }
    get in() {
        return this.ins[0];
    }
    // eslint-disable-next-line class-methods-use-this
    get out() {
        return DateAggregateConfig_1.out;
    }
    // eslint-disable-next-line class-methods-use-this
    get ins() {
        return DateAggregateConfig_1.ins;
    }
    // eslint-disable-next-line class-methods-use-this
    get outs() {
        return DateAggregateConfig_1.outs;
    }
    static get in() {
        return DateAggregateConfig_1.ins[0];
    }
    static get out() {
        return DateAggregateConfig_1.outs[0];
    }
    static get ins() {
        return ['default'];
    }
    static get outs() {
        return ['default'];
    }
    static get info() {
        return new ToolConfigInfo(ToolType.DATE_AGGREGATE.split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '), [ToolCategories.CALCULATE], this.ins, this.outs, ['dateaggregate', 'month', 'year', 'day'], true);
    }
    applyParameters(parameters) {
        const baseVals = this.getBaseValues(parameters);
        return new DateAggregateConfig_1(baseVals.dateColumn, baseVals.frequency, this.aggColumn.applyParameters(parameters), baseVals.groupByColumns, this.customFormats);
    }
    getBaseValues(parameters) {
        return {
            dateColumn: parameters.getStrVal(this.dateColumn),
            frequency: parameters.getEnumVal(this.frequency),
            aggColumn: this.aggColumn.getBaseValues(parameters),
            groupByColumns: parameters.getStrArrayOrUndefVals(this.groupByColumns)
        };
    }
    getOutput(iConfigHelper, ins) {
        const tsb = new ToolScopeBuilder(ins);
        const source = ins.schemas.at(this.in);
        if (source) {
            const baseVals = this.getBaseValues(iConfigHelper.getParameters());
            const exp = tsb.export(this.out);
            const newColumnFormats = new Map();
            const dependents = this.getGroupByDependents(exp, this.in, baseVals.groupByColumns);
            dependents.push(this.getDependent(exp, this.in, baseVals.dateColumn));
            exp.makeNew(new Column(baseVals.dateColumn, 'date'), dependents, true);
            const dateColumnMetaData = exp.findSourceColumn(this.in, baseVals.dateColumn);
            if (dateColumnMetaData?.customFormat) {
                newColumnFormats.set(baseVals.dateColumn, dateColumnMetaData.customFormat.copyWithInheritedTrue());
            }
            if (baseVals.aggColumn.operation === AggregateColumn.Operation.COUNT) {
                exp.makeNew(new Column(baseVals.aggColumn.alias, 'number'), dependents, true);
            }
            else {
                const srcColumn = source.find((s) => s.name === baseVals.aggColumn.column);
                exp.makeNew(new Column(baseVals.aggColumn.alias, srcColumn.type), dependents, true);
                if (srcColumn?.customFormat) {
                    newColumnFormats.set(baseVals.aggColumn.alias, srcColumn.customFormat.copyWithAutoGeneratedTrue());
                }
            }
            exp
                .initializeColumnFormats()
                .setColumnFormats(newColumnFormats)
                .setColumnFormats(this.customFormats)
                .setUniqueKeys(baseVals.groupByColumns);
        }
        return tsb.build();
    }
    isValidForCalc(iConfigHelper, scope) {
        const toolValidationResult = this.getToolValidationResult(iConfigHelper, scope);
        return toolValidationResult.validationState;
    }
    getToolValidationResult(iConfigHelper, scope) {
        const source = scope.schemas.at(this.in);
        if (!source) {
            return ToolValidationResult.getMissingSourceColumnsResult();
        }
        const baseVals = this.getBaseValues(iConfigHelper.getParameters());
        const outputs = baseVals.groupByColumns ? [...baseVals.groupByColumns] : [];
        const parameters = iConfigHelper.getParameters();
        const colApplied = parameters.getStrVal(baseVals.aggColumn.column);
        outputs.push(colApplied, baseVals.aggColumn.alias, baseVals.dateColumn);
        if (new Set(outputs).size !== outputs.length) {
            if (colApplied === baseVals.aggColumn.alias) {
                return ToolValidationResult.getBadMetadataResult(`The aggregate column name and alias are the same. Column name: ${colApplied}.`, 'Please ensure that the column name and alias are different. Try changing the alias.');
            }
            if (baseVals.dateColumn === baseVals.aggColumn.alias) {
                return ToolValidationResult.getBadMetadataResult(`The date column name and alias are the same. Column name: ${baseVals.dateColumn}.`, 'Please ensure that the date column name and alias are different. Try changing the alias.');
            }
            if (baseVals.groupByColumns?.includes(colApplied)) {
                return ToolValidationResult.getBadMetadataResult(`The aggregate column is also in the group by columns. Column name: ${colApplied}.`, 'Please ensure that the column in the group by columns are distinct from the aggregate column.');
            }
            if (baseVals.groupByColumns?.includes(baseVals.dateColumn)) {
                return ToolValidationResult.getBadMetadataResult(`The date column is also in the group by columns. Column name: ${baseVals.dateColumn}.`, 'Please ensure that the column in the group by columns are distinct from the date column.');
            }
            if (baseVals.groupByColumns?.includes(baseVals.aggColumn.alias)) {
                return ToolValidationResult.getBadMetadataResult(`The aggregate column alias is also in the group by columns. Column name: ${baseVals.aggColumn.alias}.`, 'Please ensure that the column in the group by columns are distinct from the aggregate column alias. Try changing the alias.');
            }
            if (colApplied === baseVals.dateColumn) {
                return ToolValidationResult.getBadMetadataResult(`The aggregate column is also the date column. Column name: ${colApplied}.`, 'Please ensure that the aggregate column is distinct from the date column.');
            }
            const duplicates = outputs.filter((item, index) => outputs.indexOf(item) !== index);
            return ToolValidationResult.getBadMetadataResult(`Duplicate column names found in the output columns. Duplicates: ${duplicates.join(', ')}`, `Please ensure that all column names are unique in the date column, aggregate column, aggregate column alias, and group by columns.`);
        }
        const boolOps = new Set([AggregateColumn.Operation.OR, AggregateColumn.Operation.AND]);
        if (boolOps.has(baseVals.aggColumn.operation)) {
            return ToolValidationResult.getBadMetadataResult('Boolean operations are not supported');
        }
        const inputs = new Map([[baseVals.dateColumn, ['date']]]);
        if (baseVals.aggColumn.column !== undefined) {
            inputs.set(baseVals.aggColumn.column, ['number', 'decimal']);
        }
        const sourceValidationResult = validateSourceWithDetail(source, inputs, []);
        return ToolValidationResult.getResultFromSourceValidation(sourceValidationResult);
    }
};
DateAggregateConfig = DateAggregateConfig_1 = __decorate([
    JsonSchemaV2(dateAggregateConfigSchema, dateAggregateConfigSchemaValidation),
    JsonDiscriminatorValue(ToolType.DATE_AGGREGATE)
], DateAggregateConfig);
export { DateAggregateConfig };
//# sourceMappingURL=dateAggregateConfig.js.map